import React, { useMemo, useState } from 'react'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { Controller } from 'react-hook-form'
import type { Control, FieldValues, FieldErrors } from 'react-hook-form'
import { industryClassifications } from '../../consts'

// IAM #industry_classification_code dropdown. 74 GICS L3 industries is
// too many for a flat <select>; this is a searchable combobox that
// matches by industry name, sector hint, or GICS code, grouped by
// sector with sticky headers.
//
// API mirrors the rest of the Onboard form: takes a react-hook-form
// `control` + `name`, wires through `Controller` so the value lives in
// the form state under that name. `errors` is the same FieldErrors
// object passed to the other fields.

interface IndustryOption {
  code: string
  name: string // industry name without sector
  sector: string
  label: string // full label as stored in consts (industry + sector hint)
}

// Parse the labels from consts.ts back into `{ name, sector }`. The
// consts source-of-truth is `"Industry Name (Sector)"`; doing it here
// rather than introducing a second taxonomy keeps the two in lock-step.
const OPTIONS: IndustryOption[] = Object.entries(industryClassifications).map(([code, label]) => {
  const match = /^(.*) \(([^)]+)\)$/.exec(label)
  return {
    code,
    name: match?.[1] ?? label,
    sector: match?.[2] ?? '',
    label,
  }
})

interface Props {
  name: string
  control: Control<FieldValues>
  errors: FieldErrors
  required?: boolean
  disabled?: boolean
}

export default function IndustryCombobox({ name, control, errors, required = true, disabled }: Props) {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') return OPTIONS
    return OPTIONS.filter(o =>
      o.name.toLowerCase().includes(q) ||
      o.sector.toLowerCase().includes(q) ||
      o.code.includes(q)
    )
  }, [query])

  // Group filtered options by sector, preserving sector insertion order
  // from OPTIONS (which is sector-then-alpha; see consts.ts).
  const grouped = useMemo(() => {
    const order: string[] = []
    const bySector: Record<string, IndustryOption[]> = {}
    filtered.forEach(o => {
      if (!bySector[o.sector]) {
        bySector[o.sector] = []
        order.push(o.sector)
      }
      bySector[o.sector].push(o)
    })
    return order.map(sector => ({ sector, items: bySector[sector] }))
  }, [filtered])

  const error = errors[name]?.message as string | undefined

  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: 'Required' } : undefined}
      render={({ field }) => {
        const selected = OPTIONS.find(o => o.code === field.value) ?? null
        return (
          <div>
            <Combobox
              value={selected}
              onChange={(opt: IndustryOption | null) => field.onChange(opt?.code ?? '')}
              disabled={disabled}
            >
              <div className="relative">
                <ComboboxInput
                  id={name}
                  className={`block w-full rounded-md border bg-white py-2 pl-3 pr-10 text-sm
                    text-gray-900 placeholder:text-gray-400 focus:outline-hidden focus:ring-2
                    focus:ring-perfect-shine focus:border-perfect-shine disabled:bg-gray-50
                    disabled:text-gray-500 ${error ? 'border-cerise-red-500' : 'border-gray-300'}`}
                  onChange={e => setQuery(e.target.value)}
                  onBlur={field.onBlur}
                  displayValue={(opt: IndustryOption | null) => opt?.label ?? ''}
                  placeholder="Search 74 industries..."
                  autoComplete="off"
                />
                <ComboboxButton
                  aria-label="Open industry picker"
                  className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-500
                    hover:text-gray-700 focus:outline-hidden"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </ComboboxButton>

                <ComboboxOptions
                  className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md
                    border border-gray-200 bg-white py-1 text-sm shadow-lg
                    focus:outline-hidden"
                >
                  {grouped.length === 0 && (
                    <div className="px-3 py-2 text-gray-500">No matching industries</div>
                  )}

                  {grouped.map(({ sector, items }) => (
                    <div key={sector}>
                      <div className="sticky top-0 z-10 bg-gray-50 px-3 py-1 text-xs font-semibold
                        uppercase tracking-wide text-gray-500">
                        {sector}
                      </div>
                      {items.map(opt => (
                        <ComboboxOption
                          key={opt.code}
                          value={opt}
                          className={({ focus, selected: isSelected }) => `
                            cursor-pointer px-3 py-2 flex items-center justify-between
                            ${focus ? 'bg-perfect-shine-50 text-perfect-shine-900' : 'text-gray-900'}
                            ${isSelected ? 'font-semibold' : 'font-normal'}
                          `}
                        >
                          <span>{opt.name}</span>
                          <span className="text-xs text-gray-400 tabular-nums">{opt.code}</span>
                        </ComboboxOption>
                      ))}
                    </div>
                  ))}
                </ComboboxOptions>
              </div>
            </Combobox>

            {error && <p className="mt-1 text-xs text-cerise-red-600">{error}</p>}
          </div>
        )
      }}
    />
  )
}
